import React, { useCallback, useEffect, useState } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { LocationCoords, FareResponse, SelectionMode } from '../../types/fare';
import { hasRouteInfo } from '../../types/fare';
import { calculateBoundsForRoutes } from '../../utils/polylineUtils';
import { SRI_LANKA_CENTER, DEFAULT_ZOOM } from '../../constants/farePresets';
import CustomMarkers from './CustomMarkers';
import RawPolylines from './RawPolylines';
import FareOverlay from './FareOverlay';
import { GOOGLE_MAP_ID } from '../../config';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
};

interface FareMapContainerProps {
  pickupLocation: LocationCoords | null;
  destinationLocation: LocationCoords | null;
  fareData: FareResponse | null;
  selectedRouteIndex: number;
  onRouteSelect: (index: number) => void;
  selectionMode: SelectionMode;
  onMapClick: (location: LocationCoords) => void;
  polylinesKey?: number;
  clearingRoutes?: boolean;
  mapId?: string; // Add identifier for debugging
}

export default function FareMapContainer({
  pickupLocation,
  destinationLocation,
  fareData,
  selectedRouteIndex,
  onRouteSelect,
  selectionMode,
  onMapClick,
  polylinesKey = 0,
  clearingRoutes = false,
  mapId = 'unknown',
}: FareMapContainerProps) {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [center, setCenter] = useState<LocationCoords>(SRI_LANKA_CENTER);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [shouldShowRoutes, setShouldShowRoutes] = useState(false);

  // Track component lifecycle
  useEffect(() => {
    return () => {
      // cleanup on unmount
    };
  }, [mapId]);

  // Control route visibility - MUST check fareData exists first
  useEffect(() => {
    // ALWAYS hide when clearing, regardless of fareData
    if (clearingRoutes) {
      setShouldShowRoutes(false);
      return;
    }
    
    // If no valid data, hide routes
    if (!fareData || !hasRouteInfo(fareData)) {
      setShouldShowRoutes(false);
      return;
    }

    // We have valid data and not clearing - show routes after small delay
    const timer = setTimeout(() => {
      setShouldShowRoutes(true);
    }, 50);
    
    return () => clearTimeout(timer);
  }, [fareData, clearingRoutes, mapId]);

  // Force shouldShowRoutes to false when polylinesKey changes
  useEffect(() => {
    setShouldShowRoutes(false);
  }, [polylinesKey, mapId]);

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, [mapId]);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, [mapId]);

  const handleMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (selectionMode !== 'none' && e.latLng) {
        const location: LocationCoords = {
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        };
        onMapClick(location);
      }
    },
    [selectionMode, onMapClick]
  );

  // Fit bounds when routes are loaded
  useEffect(() => {
    if (map && fareData && hasRouteInfo(fareData)) {
      const { route } = fareData;
      const polylines = [
        route.recommended.polyline,
        ...route.alternatives.map((alt) => alt.polyline),
      ].filter((p): p is string => p !== null);

      if (polylines.length > 0) {
        const bounds = calculateBoundsForRoutes(polylines);
        if (bounds) {
          map.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 });
        }
      }
    } else if (map && pickupLocation && destinationLocation) {
      // If no routes but we have both locations, fit to them
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(new google.maps.LatLng(pickupLocation.lat, pickupLocation.lng));
      bounds.extend(new google.maps.LatLng(destinationLocation.lat, destinationLocation.lng));
      map.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 });
    } else if (map && pickupLocation) {
      // Just pickup, center on it
      setCenter(pickupLocation);
      setZoom(13);
    }
  }, [map, fareData, pickupLocation, destinationLocation]);

  // Get cursor class based on selection mode
  const getCursorClass = () => {
    if (selectionMode === 'pickup') return 'cursor-pickup-pin';
    if (selectionMode === 'destination') return 'cursor-destination-pin';
    return '';
  };

  return (
    <div className={`relative w-full h-full ${getCursorClass()}`}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={zoom}
        options={{
          mapId: GOOGLE_MAP_ID, // Add mapId for Dynamic Maps pricing
          ...mapOptions,
          styles: selectionMode !== 'none' ? [
            {
              featureType: 'all',
              elementType: 'all',
              stylers: [{ saturation: -20 }],
            },
          ] : undefined,
          draggableCursor: selectionMode !== 'none' ? 'crosshair' : undefined,
        }}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={handleMapClick}
      >
        {/* Markers */}
        <CustomMarkers pickup={pickupLocation} destination={destinationLocation} />

        {/* Route polylines and overlays - ONLY render when ALL conditions met */}
        {(() => {
          const shouldRender = !clearingRoutes && fareData && hasRouteInfo(fareData) && shouldShowRoutes;
          
          return shouldRender ? (
            <>
              {/* Raw polylines - Direct Google Maps API (bypasses React wrapper bug) */}
              <RawPolylines
                key={`polylines-${polylinesKey}-${fareData!.timestamp}`}
                map={map}
                routeInfo={fareData!.route}
                selectedRouteIndex={selectedRouteIndex}
                onRouteSelect={onRouteSelect}
                mapId={mapId}
              />

            {/* Fare overlays on routes */}
            {[fareData.route.recommended, ...fareData.route.alternatives].map((route, index) => {
              if (selectedRouteIndex === index) return null;
              return (
                <FareOverlay
                  key={`overlay-${polylinesKey}-${index}`}
                  route={route}
                  routeIndex={index}
                  isSelected={false}
                  onSelect={() => onRouteSelect(index)}
                />
              );
            })}
            {/* Render selected overlay last (on top) */}
            {(() => {
              const routes = [fareData.route.recommended, ...fareData.route.alternatives];
              const selectedRoute = routes[selectedRouteIndex];
              if (selectedRoute) {
                return (
                  <FareOverlay
                    key={`overlay-selected-${polylinesKey}-${selectedRouteIndex}`}
                    route={selectedRoute}
                    routeIndex={selectedRouteIndex}
                    isSelected={true}
                    onSelect={() => onRouteSelect(selectedRouteIndex)}
                  />
                );
              }
              return null;
            })()}
            </>
          ) : null;
        })()}
      </GoogleMap>

      {/* Selection instruction overlay */}
      {selectionMode !== 'none' && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white px-6 py-3 rounded-lg shadow-lg z-10">
          <p className="text-sm font-medium text-gray-900">
            {selectionMode === 'pickup'
              ? '📍 Click on the map to select pickup location'
              : '📍 Click on the map to select destination'}
          </p>
        </div>
      )}
    </div>
  );
}

